<template>
  <div class="offcanvas offcanvas-end" id="OffcanvasForm" ref="offcanvas" data-bs-backdrop="static">
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE'? $t('Create Expense Category'): $t('Edit Expense Category') }}
      <button type="button" style="font-size: small;" class="btn-close shadow-none text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Expense Category Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label for="name" class="form-label">
              {{ $t('Category Name') }} <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="name"
                type="text"
                v-model.trim="v$.form.name.$model"
                class="form-control shadow-none"
                :placeholder="$t('e.g. Rent') + '...'"
              >
              <div v-for="error of v$.form.name.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>
          <!--
            ตั้งครั้งเดียวที่ประเภท แล้วทุกค่าใช้จ่ายที่เลือกประเภทนี้จะได้ค่านี้เป็นค่าเริ่มต้น
            (silver-spoon-api/docs/vat-compliance.md D3) — พนักงานคีย์รายวันจึงไม่ต้องตอบคำถามภาษี
          -->
          <div class="col-12">
            <div class="form-check form-switch">
              <input
                id="is_vat_claimable"
                class="form-check-input"
                type="checkbox"
                v-model="form.is_vat_claimable"
              >
              <label class="form-check-label" for="is_vat_claimable">
                {{ $t('Input VAT can be claimed') }}
              </label>
            </div>
            <div class="form-text">
              {{ $t('Turn off for entertainment expenses or passenger-car fuel — their input VAT cannot be claimed.') }}
            </div>
            <div class="form-text text-muted">
              {{ $t('Changing this only affects expenses recorded from now on.') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-light border-top justify-content-center modal-footer py-2" v-if="!loading && hasPermission">
      <ladda
        text="Save"
        v-if="type === 'CREATE'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleCreate"
      />
      <ladda
        text="Save"
        v-if="type === 'EDIT'"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button type="button" data-bs-dismiss="offcanvas" class="border-dark-subtle btn-sm btn btn-light me-2 ms-2">
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { ExpenseCategories } from '@/services/endpoint/expense-categories.service';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: { $lazy: true },
  validations() {
    return validation(this.form)
  },
  props: {
    type: { type: String, default: 'CREATE' },
    id: { type: Number, default: 0 },
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        name: '',
        // ค่าเริ่มต้นของประเภทใหม่ — ค่าใช้จ่ายส่วนใหญ่ขอคืนภาษีซื้อได้
        is_vat_claimable: true
      }
    }
  },
  components: { PlaceholderContent, Ladda, AccessDenied },
  methods: {
    async getUpdateData() {
      await ExpenseCategories.getUpdate(this.id).then(res => {
        this.form = {
          name: res.data.data.name,
          is_vat_claimable: res.data.data.is_vat_claimable !== false
        };
        this.hasPermission = true;
      }).catch(err => { throw err; });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      await ExpenseCategories.update(this.id, this.form).then(() => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
        this.loadingSave = false;
      });
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      await ExpenseCategories.create(this.form).then(() => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(this.$t(err?.response?.data?.message));
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') this.hasPermission = true;
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {}
    });
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
}
</script>
